import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { ControlContainer, UntypedFormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Guid } from 'guid-typescript';
import { AddressModel } from 'src/app/models/address.model';
import { LocationSummaryModel } from 'src/app/models/locationSummary.model';
import { CoordinatesModel } from 'src/app/models/coordinates.model';
import { ClientService } from 'src/app/services/client.services';
import { GeolocationService } from 'src/app/services/geolocation.service';
import { AddressComponent } from 'src/app/shared/components/address/address.component';
import { AddressTypeEnum } from 'src/app/shared/enumerators/address-type.enum';
import { ToastService } from 'src/app/services/toast.service';
import { GeolocationHelperService } from 'src/app/services/geolocation-helper.service';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { AddressComponent as AddressComponent_1 } from '../../../shared/components/address/address.component';
import { InduMapComponent } from '../../../shared/components/indu-map/indu-map.component';

@Component({
    selector: 'app-step-address',
    templateUrl: './step-address.component.html',
    styleUrls: ['./step-address.component.scss'],
    viewProviders: [
        { provide: ControlContainer, useExisting: FormGroupDirective },
    ],
    imports: [NgClass, MatIcon, FormsModule, ReactiveFormsModule, AddressComponent_1, InduMapComponent]
})
export class StepAddressComponent implements OnInit {
  private readonly geolocationService = inject(GeolocationService);
  private readonly geolocationHelperService = inject(GeolocationHelperService);
  toastr = inject(ToastService);
  private readonly clientService = inject(ClientService);


  @ViewChild(AddressComponent, { static: false })
  addressComponent!: AddressComponent;

  @Input() workloadForm: UntypedFormGroup = new UntypedFormGroup({});

  coordinates: CoordinatesModel | undefined;
  disableCoordinates: boolean = true;
  coordinatesInterval: any;

  clientAddresses: AddressModel[] = [];
  selectedAddressId: Guid = Guid.createEmpty();

  legalAddress: string | undefined;

  //google maps initialization test params
  markers: any[] = [];
  center: google.maps.LatLngLiteral = {
    lat: -31.4492878,
    lng: -64.177427,
  };

  ngOnInit(): void {
    this.legalAddress = 'legalAddress';
  }

  ngAfterViewInit(): void {
    this.addressComponent.formControl.valueChanges.subscribe(
      (model: AddressModel) => {
        this.enableMapButton(model);
      }
    );
  }

  // addressIdToSelect: the address to start with (e.g. "Generar tarea" from a task: same client, same address).
  // It is selected once its city is loaded, because selecting fills the state and city from it.
  getAddresses(clientId: Guid, addressIdToSelect?: string) {
    this.clientService
      .getAddresses(clientId)
      .subscribe((data: AddressModel[]) => {
        if (data) {
          this.clientAddresses = data;
          this.clientAddresses.forEach((ca) => {
            this.geolocationService
              .getCity(ca.cityId, true)
              .subscribe((data: LocationSummaryModel) => {
                ca.locationSummary = data;
                if (
                  addressIdToSelect &&
                  String(ca.id) === String(addressIdToSelect) &&
                  String(this.selectedAddressId) !== String(ca.id)
                ) {
                  this.selectAddress(ca);
                }
              });
          });
        }
      });
  }

  selectAddress(address: AddressModel) {
    if (this.selectedAddressId == address.id) {
      this.selectedAddressId = Guid.createEmpty();
      this.addressComponent.cleanForm();
      this.coordinates = { lat: 0, long: 0 };
    } else {
      this.addressComponent.overwriteStates(address.locationSummary.state);
      this.addressComponent.overwriteCities(address.locationSummary);
      this.selectedAddressId = address.id;
      this.addressComponent.patchValue(
        AddressTypeEnum.LegalAddress,
        address,
        true
      );
      this.enableMapButton(address);
      this.coordinates = { lat: address.latitude, long: address.longitude };
    }
    this.addMarker();
  }

  cleanStep() {
    this.addressComponent.cleanForm();
    this.clientAddresses = [];
    this.selectedAddressId = Guid.createEmpty();
  }

  addMarker() {
    const { markers, center } = this.geolocationHelperService.generateMarker(
      this.coordinates
    );

    this.markers = markers;
    if (center) {
      this.center = center;
    }
  }

  addPointerMarker() {
    this.geolocationHelperService.addPointerMarker(
      this.addressComponent,
      this.getCoordinates.bind(this)
    );
  }

  getCoordinates(
    street: string,
    streetNumber: number,
    city: any,
    state: any,
    country: any
  ) {
    this.geolocationHelperService
      .getAndSetCoordinates(
        street,
        streetNumber,
        city,
        state,
        country,
        this.addressComponent
      )
      .subscribe((data: CoordinatesModel) => {
        this.coordinates = data;
        this.addMarker();
      });
  }

  enableMapButton(model: AddressModel) {
    if (
      model.street &&
      model.street.length > 0 &&
      model.number > 0 &&
      model.cityId &&
      Guid.isGuid(model.cityId) &&
      model.stateId &&
      Guid.isGuid(model.stateId) &&
      model.countryId &&
      Guid.isGuid(model.countryId)
    ) {
      this.disableCoordinates = false;
    } else {
      this.disableCoordinates = true;
      this.coordinates = { lat: 0, long: 0 };
      this.addMarker();
    }
  }

  markerDragend(type: any, newPoint: google.maps.LatLngLiteral) {
    this.addressComponent.setCoordinates(type, newPoint.lat, newPoint.lng);
  }

  isValidAddress(type: any): boolean {
    let isValid = this.addressComponent.isValidAddress(type);
    let form = this.addressComponent.getForm(type);

    if (
      !isValid &&
      !this.isDisabledAddressForm(type) &&
      (form.controls.latitude?.value == null ||
        form.controls.longitude?.value == null)
    ) {
      this.toastr.error('Error', 'Debe buscar la ubicación.');
    }

    return isValid;
  }

  isDisabledAddressForm(type: any): boolean {
    return this.addressComponent.isDisabledForm(type);
  }

  getAddressValue(type: any): AddressModel {
    return this.addressComponent.getValue(type);
  }

  markAllAsTouched(type: any) {
    this.addressComponent.markAllAsTouched(type);
  }

  onRowKeydown(event: KeyboardEvent, element: any) {
    if (event.key === ' ') {
      this.selectAddress(element);
    }
  }
}
